import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Copy, X, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ShareDialog({ open, onClose, character }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    setBusy(true);
    setLink("");
    setCopied(false);
    try {
      // Strip internal fields, keep the display data
      const snapshot = { ...character };
      delete snapshot.user_id;
      const r = await api.post("/share", { character: snapshot });
      const url = `${window.location.origin}/share/${r.data.share_id}`;
      setLink(url);
      toast.success("SHARE LINK MINTED");
    } catch {
      toast.error("SHARE FAILED", { description: "Backend unreachable — try again." });
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("COPIED TO CLIPBOARD");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("COPY BLOCKED — select the link manually.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="share-modal">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/85 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 22 }}
            className="relative panel panel-cyan p-6 w-full max-w-lg"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Share2 className="text-[color:var(--accent-cyan)]" />
                <div className="font-display uppercase font-black tracking-tight text-xl">PARTY SHARE LINK</div>
              </div>
              <button data-testid="share-close" onClick={onClose} aria-label="Close" className="p-1 hover:text-[color:var(--accent-magenta)]">
                <X size={18} />
              </button>
            </div>

            <div className="font-mono text-xs text-[color:var(--text-secondary)] uppercase tracking-widest mb-3">
              Mint a read-only snapshot of this punk. Share the URL with your GM — they see stats, gear and loadout but can't edit.
            </div>

            {!link ? (
              <button
                data-testid="share-generate-btn"
                onClick={generate}
                disabled={busy}
                className="cy-btn cy-btn-solid-yellow w-full"
              >
                <Share2 size={16} className="mr-2" /> {busy ? "MINTING…" : "GENERATE SHARE LINK"}
              </button>
            ) : (
              <>
                <div className="border-2 border-[color:var(--accent-yellow)] p-3 mb-3 font-mono text-xs break-all" data-testid="share-link-box">
                  {link}
                </div>
                <div className="flex gap-2">
                  <button data-testid="share-copy-btn" onClick={copy} className="cy-btn cy-btn-cyan text-xs flex-1">
                    {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                    {copied ? "COPIED" : "COPY LINK"}
                  </button>
                  <button data-testid="share-regen-btn" onClick={generate} disabled={busy} className="cy-btn text-xs">
                    RE-MINT
                  </button>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[color:var(--text-muted)] mt-3">
                  Note: this is a snapshot. Edits made after minting won't appear until you re-mint the link.
                </div>
              </>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
